<script lang="ts" setup>
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import i18n from "@/locales";
import { useMate } from "@/stores/mate";
import { useUI } from "@/stores/ui";
import { Mate } from "@/types/entity";
import { showConfirmDialog, showLoadingToast } from "vant";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import EmptyContent from "@/components/mobile/common/EmptyContent.vue";

const props = withDefaults(
  defineProps<{
    title?: string;
    mateOnly?: boolean;
    selectionMode?: "selection" | "edit";
    exclude?: number[];
    callback?: (mate: Mate) => void;
    switchMate?: boolean;
    showUnpublished?: boolean;
    onClick?: (mate: Mate) => void;
    selectedMateId?: number;
    includeSelfAvatar?: boolean;
  }>(),
  {
    mateOnly: false,
    selectionMode: "selection",
    exclude: () => [],
    callback: () => {},
    switchMate: true,
    showUnpublished: false,
    includeSelfAvatar: false,
  }
);

const mateStore = useMate();
const router = useRouter();
const route = useRoute();
const uiStore = useUI();

const isHomeDiscover = computed(() => route.name === "threads");

const ocList = computed<Mate[]>(() => {
  return mateStore.list.filter(
    (mate) =>
      !props.exclude.includes(mate.id) &&
      !(
        props.selectionMode === "selection" &&
        !props.showUnpublished &&
        mate.status === 0
      )
  );
});

const onChangeMate = async (mateId: number) => {
  const toast = showLoadingToast({
    message: i18n.global.t("Message.switchingCharacters"),
    duration: 0,
  });
  try {
    await mateStore.changeCurrentMate(mateId);
    uiStore.showMateSelector = false;
  } finally {
    toast.close();
  }
};

const onMateItemClicked = async (mate: Mate) => {
  if (props.onClick) {
    props.onClick(mate);
    return;
  }

  if (props.selectionMode === "edit") {
    if (mate.status === 0) {
      await onChangeMate(mate.id);
      router.push({ name: "mateEdit", params: { uuid: mate.uuid } });
    } else if (mateStore.currentMate?.id === mate.id) {
      return;
    } else {
      // 1. 当前路由不允许Mate切换：确认弹窗 - 路由跳转 - 切换
      if (!route.meta.enableSwitchMate) {
        try {
          await showConfirmDialog({
            title: i18n.global.t("ConfirmModal.notSupportMateSwitchTitle"),
            message: i18n.global.t("ConfirmModal.notSupportMateSwitchDesc"),
            confirmButtonText: i18n.global.t("Buttons.confirm"),
            cancelButtonText: i18n.global.t("Buttons.cancel"),
          });
          await window.$router.push({ name: "interactive" });
          await onChangeMate(mate.id);
        } catch {
          return;
        }
      } else {
        // 2. 当前路由允许Mate切换：直接切换
        await onChangeMate(mate.id);
      }
    }
  } else {
    if (props.switchMate) {
      await onChangeMate(mate.id);
    }
    props.callback?.(mate);
  }
};

const isSelected = (mateId: number) => {
  if (props.selectedMateId) {
    return props.selectedMateId === mateId;
  }
  return mateStore.currentMate?.id === mateId && props.selectionMode === "edit";
};
</script>

<template>
  <div :class="['grid grid-rows-[auto_1fr] overflow-hidden']">
    <div
      class="w-full flex items-center justify-center py-3 text-base-content border-b font-bold flex-col"
    >
      <template v-if="title">
        {{ title }}
      </template>
      <template v-else-if="isHomeDiscover">
        <span>{{ $t("User.mate.selectRole") }}</span>
        <span class="text-base-content/50 text-sm">
          {{ $t("User.mate.selectRoleDesc") }}
        </span>
      </template>
      <template v-else>
        {{ $t("User.mate.selectCharacterToCreate") }}
      </template>
    </div>
    <div class="overflow-y-auto h-full flex flex-col">
      <template v-if="!mateOnly">
        <h3 class="font-bold px-4 mt-4">
          {{ $t(`Titles.me`) }}
        </h3>
        <div
          class="flex px-4 py-2 items-center justify-between border-b-neutral-content/5 border-b"
          @click="onMateItemClicked(mateStore.userMate)"
        >
          <div class="flex items-center gap-4">
            <MateInfo :mate="mateStore.userMate" show-unread />
          </div>
          <div class="flex flex-col gap-2 items-center">
            <div
              class="flex gap-2 items-center flex-nowrap shrink-0 justify-center"
            >
              <i
                v-if="
                  selectedMateId !== undefined
                    ? isSelected(mateStore.userMate.id)
                    : mateStore.currentMate?.id === mateStore.userMate.id
                "
                class="iconfont icon-check-circle-fill text-success"
              />
              <button v-else class="btn btn-sm">
                {{ $t("Buttons.select") }}
              </button>
            </div>
            <div class="join">
              <button
                class="btn btn-sm join-item"
                @click.stop="
                  router.push({
                    name: 'mateHome',
                    params: { uuid: mateStore.userMate.uuid },
                  })
                "
              >
                <i class="iconify mingcute--user-4-line" />
              </button>
            </div>
          </div>
        </div>
      </template>
      <template v-if="includeSelfAvatar">
        <div
          v-for="mate in mateStore.selfAvatarList"
          :key="mate.id"
          class="flex px-4 py-2 items-center justify-between border-b"
          @click="onMateItemClicked(mate)"
        >
          <div class="flex items-center gap-4">
            <MateInfo :mate="mate" show-unread />
          </div>
          <div class="flex flex-col gap-2 items-center">
            <div
              class="flex gap-2 items-center flex-nowrap shrink-0 justify-center"
            >
              <button
                v-if="
                  mate.status === 0 &&
                  selectionMode === 'selection' &&
                  !showUnpublished
                "
                class="btn btn-sm rounded text-base-content/50 btn-ghost font-normal"
              >
                {{ $t("User.mate.completeProfile") }}
                <light-arrow-icon class="w-2.5 rotate-180" />
              </button>
              <i
                v-else-if="isSelected(mate.id)"
                class="iconfont icon-check-circle-fill text-success"
              />
              <button v-else class="btn btn-sm">
                {{ $t("Buttons.select") }}
              </button>
            </div>
            <div class="join">
              <button
                v-if="mate.status !== 0"
                class="btn btn-sm join-item"
                @click.stop="mateStore.toMateHome(mate)"
              >
                <i class="iconify mingcute--user-4-line" />
              </button>
            </div>
          </div>
        </div>
      </template>
      <h3 class="font-bold px-4 mt-4">
        {{ $t(`Titles.mymates`) }}
      </h3>
      <div
        v-for="mate in ocList"
        :key="mate.id"
        class="flex px-4 py-2 items-center justify-between border-b"
        @click="onMateItemClicked(mate)"
      >
        <div class="flex items-center gap-4">
          <MateInfo :mate="mate" show-unread />
        </div>
        <div class="flex flex-col gap-2 items-center">
          <div
            class="flex gap-2 items-center flex-nowrap shrink-0 justify-center"
          >
            <button
              v-if="
                mate.status === 0 &&
                selectionMode === 'selection' &&
                !showUnpublished
              "
              class="btn btn-sm rounded text-base-content/50 btn-ghost font-normal"
            >
              {{ $t("User.mate.completeProfile") }}
              <light-arrow-icon class="w-2.5 rotate-180" />
            </button>
            <i
              v-else-if="isSelected(mate.id)"
              class="iconfont icon-check-circle-fill text-success"
            />
            <button v-else class="btn btn-sm">
              {{ $t("Buttons.select") }}
            </button>
          </div>
          <div class="join">
            <button
              v-if="mate.status !== 0"
              class="btn btn-sm join-item"
              @click.stop="
                router.push({
                  name: 'mateHome',
                  params: { uuid: mate.uuid },
                })
              "
            >
              <i class="iconify mingcute--user-4-line" />
            </button>
          </div>
        </div>
      </div>
      <EmptyContent
        v-if="selectionMode === 'selection' && !ocList.length"
        :title="$t('Empty.mateSelect.noCharacterAvailable')"
        :desc="$t('Empty.mateSelect.pleaseCreateNewCharacter')"
      />

      <div
        v-if="selectionMode === 'edit'"
        class="flex p-4 items-center justify-between"
        @click="mateStore.clickCreateMate()"
      >
        <div class="flex items-center gap-4 cursor-pointer">
          <i class="iconify mingcute--add-circle-fill text-3xl" />
          <div>{{ $t("Buttons.createNewCharacter") }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
